// DEPENDENCIES
import React, { useState, useEffect, useContext, useMemo } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

// CSS
import "./PendingAmendmentReviewScreen.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import {
	getAPICall,
	putAPICall,
	postAPICall,
} from "../../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// MUI COMPONENTS
import {
	Autocomplete,
	Alert,
	Button,
	CircularProgress,
	FormControl,
	FormHelperText,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// CUSTOM COMPONENTS
// import PendingJobCard from "./PendingJobCard/PendingJobCard";
import J2NoResultsFound from "../../../Misc/J2NoResultsFound/J2NoResultsFound";

// CONSTANTS
import { integerRegex } from "../../../../constants/regex";
import { generateAlphanumericCode } from "../../../../constants/constants";
import {
	getCurrentTimeUTC,
	convertToPSTYearOnly,
} from "../../../../constants/dateTime";

// REACT COMPONENT
const PendingAmendmentReviewScreen = () => {
	// MSAL
	const { instance, accounts } = useMsal();
	const navigate = useNavigate();

	// STATES
	const [pendingAmendmentRecord, setPendingAmendmentRecord] = useState({});

	const [propertyRecord, setPropertyRecord] = useState(null);
	const [projectRecord, setProjectRecord] = useState(null);
	const [phaseRecord, setPhaseRecord] = useState(null);

	const [amendmentAdjustmentAmount, setAmendmentAdjustmentAmount] =
		useState("");
	const [contractType, setContractType] = useState("");
	const [adjustmentLocation, setAdjustmentLocation] = useState("");

	const [bqePropertySearchValue, setBqePropertySearchValue] = useState("");

	const [bqePropertyRecord, setBqePropertyRecord] = useState(null);
	const [bqeProjectID, setBQEProjectID] = useState(null);
	const [bqePhaseID, setBQEPhaseID] = useState(null);

	// General States
	const [allProperties, setAllProperties] = useState([]);
	const [allProjects, setAllProjects] = useState([]);
	const [allPhases, setAllPhases] = useState([]);

	const [bqePropertyOptions, setBqePropertyOptions] = useState([]);
	const [bqeProjectOptions, setBqeProjectOptions] = useState([]);
	const [bqePhaseOptions, setBqePhaseOptions] = useState([]);

	const [isLoading, setIsLoading] = useState(false);
	const [isBQEPropertyLoading, setIsBQEPropertyLoading] = useState(false);
	const [isBQEPhaseLoading, setIsBQEPhaseLoading] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const showContractTypeMismatch = (() => {
		if (!contractType) return false;
		const expectedBqeType = contractType === "fixed_fee" ? 1 : 0;
		if (adjustmentLocation === "existing_phase") {
			if (!bqePhaseID) return false;
			const selectedPhase = bqePhaseOptions.find((p) => p.id === bqePhaseID);
			if (!selectedPhase) return false;
			return selectedPhase.contractType !== expectedBqeType;
		}
		if (adjustmentLocation === "amendment_phase") {
			const existingAmendmentPhase = bqePhaseOptions.find((p) => p.displayName?.includes("Amendment"));
			if (!existingAmendmentPhase) return false;
			return existingAmendmentPhase.contractType !== expectedBqeType;
		}
		return false;
	})();

	const adjustmentInfoMessage = (() => {
		if (!contractType || !adjustmentLocation) return null;
		const basisLabel = contractType === "fixed_fee" ? "a fixed fee basis" : "a time & cost basis";
		const amendmentAmt = Number(amendmentAdjustmentAmount) || 0;
		const fmt = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
		const amtLabel = amendmentAmt ? fmt(amendmentAmt) : "Amendment amount";

		const buildAmountSuffix = (currentAmt) => {
			if (currentAmt == null || !amendmentAmt) return "";
			return ` The current amount is ${fmt(currentAmt)} and the new amount will be ${fmt(currentAmt + amendmentAmt)}.`;
		};

		if (adjustmentLocation === "existing_phase") {
			const phase = bqePhaseOptions.find((p) => p.id === bqePhaseID);
			const phaseName = phase?.displayName || "the selected phase";
			return `${amtLabel} will be added to the existing phase ${phaseName} on ${basisLabel}.${buildAmountSuffix(phase?.contractAmount ?? null)}`;
		}
		if (adjustmentLocation === "amendment_phase") {
			const existingAmendmentPhase = bqePhaseOptions.find((p) => p.displayName?.includes("Amendment"));
			if (existingAmendmentPhase) {
				return `${amtLabel} will be added to the existing amendment phase ${existingAmendmentPhase.displayName} on ${basisLabel}.${buildAmountSuffix(existingAmendmentPhase?.contractAmount ?? null)}`;
			}
			return `${amtLabel} will be added to a new amendment phase on ${basisLabel}.`;
		}
		return null;
	})();

	const [alertMessage, setAlertMessage] = useState("");
	const [alertType, setAlertType] = useState("info");
	const [fieldErrors, setFieldErrors] = useState({});

	// PARAMS
	const { id } = useParams();

	// CONTEXT

	const employeeID = useContext(UserContext);

	const summarizeOutput = (
		value,
		seen = new WeakSet(),
		depth = 0,
		maxDepth = 4
	) => {
		if (value === undefined || value === null) return value ?? null;
		if (depth > maxDepth) return "[MaxDepthExceeded]";
		if (typeof value !== "object") return value;
		if (seen.has(value)) return "[Circular]";
		seen.add(value);

		if (Array.isArray(value)) {
			return value.map((v) =>
				summarizeOutput(v, seen, depth + 1, maxDepth)
			);
		}

		if ("status" in value || "data" in value || "error" in value) {
			return {
				status: value?.status ?? null,
				data: summarizeOutput(value?.data ?? null, seen, depth + 1),
				error:
					value?.error?.message ||
					value?.error ||
					value?.data?.error ||
					null,
			};
		}

		const redactedKeys = new Set([
			"authorization",
			"cookie",
			"set-cookie",
			"access_token",
			"refresh_token",
			"token",
		]);

		const summarized = {};
		Object.keys(value).forEach((key) => {
			if (redactedKeys.has(key.toLowerCase())) {
				summarized[key] = "[Redacted]";
				return;
			}
			summarized[key] = summarizeOutput(
				value[key],
				seen,
				depth + 1,
				maxDepth
			);
		});
		return summarized;
	};

	const submitPendingAmendmentFormLog = async ({
		actionType,
		actionStatus,
		errorMessage = null,
		actionClickedTime = null,
		actionCompletedTime = null,
		contractAmountPreSubmission = null,
		phaseID = null,
		bqePhaseID = null,
		amendmentAmount = null,
		submittedOn = null,
		submittedBy = null,
		selectedPhaseID = null,
		selectedBqePhaseID = null,
		newPhaseCreated = null,
		contractAmountAdjusted = null,
		stepOutputs = {},
	} = {}) => {
		try {
			await postAPICall(
				instance,
				accounts[0],
				"/api/logs/pendingAmendments",
				{
					actionType,
					actionStatus,
					errorMessage,
					pendingAmendmentID: pendingAmendmentRecord.id ?? null,
					submittedByEmployeeID: employeeID ?? null,
					submittedByEmployeeName:
						accounts[0]?.name || accounts[0]?.username || null,
					actionClickedTime,
					actionCompletedTime,
					hubspotDealID: pendingAmendmentRecord.hubspot_deal_id ?? null,
					hubspotDealName:
						pendingAmendmentRecord.hubspot_deal_name ?? null,
					propertyID: pendingAmendmentRecord.property_id ?? null,
					projectID: pendingAmendmentRecord.project_id ?? null,
					phaseID: phaseID ?? pendingAmendmentRecord.phase_id ?? null,
					bqePropertyID: pendingAmendmentRecord.bqe_property_id ?? null,
					bqeProjectID: pendingAmendmentRecord.bqe_project_id ?? null,
					bqePhaseID:
						bqePhaseID ?? pendingAmendmentRecord.bqe_phase_id ?? null,
					amendmentAmount:
						amendmentAmount ??
						pendingAmendmentRecord.amendment_amount ??
						null,
					contractAmountPreSubmission,
					adjustmentAmount: amendmentAdjustmentAmount || null,
					isSubmitted: pendingAmendmentRecord.is_submitted ?? null,
					isTimeAndMaterials:
						pendingAmendmentRecord.is_time_and_materials ?? null,
					newPhaseCreated:
						newPhaseCreated ?? pendingAmendmentRecord.new_phase_created ?? null,
					contractAmountAdjusted:
						contractAmountAdjusted ??
						pendingAmendmentRecord.contract_amount_adjusted ??
						null,
					submittedOn: submittedOn ?? pendingAmendmentRecord.submitted_on ?? null,
					submittedBy: submittedBy ?? pendingAmendmentRecord.submitted_by ?? null,
					selectedPropertyID: propertyRecord?.id ?? null,
					selectedProjectID: projectRecord?.id ?? null,
					selectedPhaseID: selectedPhaseID ?? phaseRecord?.id ?? null,
					selectedBqePropertyID: bqePropertyRecord?.id ?? null,
					selectedBqeProjectID: bqeProjectID ?? null,
					selectedBqePhaseID: selectedBqePhaseID ?? bqePhaseID ?? null,
					payloadSnapshot: {
						pendingAmendmentRecord,
						propertyRecord,
						projectRecord,
						phaseRecord,
						bqePropertyRecord,
						bqeProjectID,
						bqePhaseID,
						amendmentAdjustmentAmount,
						stepOutputs: summarizeOutput(stepOutputs),
					},
					modifiedBy: employeeID ?? null,
				}
			);
		} catch (error) {
			console.error("Failed to submit pending amendment form log:", error);
		}
	};

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Pending Amendment Review",
			employeeID: employeeID,
			notes: "Accessed the Pending Amendment Review page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchInitialData();
	}, []);

	const formatNameWithYear = (record) => {
		if (!record?.name) return "";
		const year = record.created ? convertToPSTYearOnly(record.created) : "";
		return year ? `${record.name} (${year})` : record.name;
	};

	// INPUT HANDLERS
	const handlePropertyChange = (event, newValue) => {
		clearFieldError("property");
		setPropertyRecord(newValue);
		setProjectRecord(null);
		setPhaseRecord(null);
	};

	const handleProjectChange = (event, newValue) => {
		clearFieldError("project");
		setProjectRecord(newValue);
		setPhaseRecord(null);
	};

	const handlePhaseChange = (event, newValue) => {
		clearFieldError("phase");
		setPhaseRecord(newValue);
	};

	const bqePropertyNameChanged = useMemo(
		() =>
			debounce(async (event) => {
				const textLength = event.target.value.length;

				// if the current text length is less than 3, we will get too many results, so return
				if (textLength < 3) {
					setBqePropertyOptions([]);
					return;
				}

				setIsBQEPropertyLoading(true);

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/bqe/property/byName",
						{
							propertyName: event.target.value,
						}
					);

					setBqePropertyOptions(
						res.data == ""
							? []
							: res.data.filter(
									(option) =>
										!option.displayName.endsWith(
											"(deleted)"
										)
							  )
					);
				} catch (error) {
					setBqePropertyOptions([]);
					console.error("Error in propertyNameChanged: ", error);
				}

				setIsBQEPropertyLoading(false);
			}, 500), // 500ms debounce
		[]
	);

	const bqePropertySelected = (event, newValue) => {
		if (newValue && newValue.displayName) {
			clearFieldError("bqeProperty");
			// if a deal name is selected and the user didn't just clear the autocomplete
			setBqePropertyRecord(newValue);
			setBqePropertySearchValue(newValue.displayName);

			fetchBQEProjects(newValue.id);
		} else {
			// if the user cleared the autocomplete
			setBqePropertyRecord(null);
			setBqePropertySearchValue("");

			setBqePropertyOptions([]);

			setBqeProjectOptions([]);
			setBQEProjectID(null);
			setBqePhaseOptions([]);
			setBQEPhaseID(null);
		}
	};

	const bqeProjectSelected = (event) => {
		const selectedProjectID = event.target.value;
		clearFieldError("bqeProject");

		if (selectedProjectID) {
			setBQEProjectID(selectedProjectID);
			setBQEPhaseID(null);
			fetchBQEPhases(selectedProjectID);
		} else {
			setBQEProjectID(null);
			setBqePhaseOptions([]);
			setBQEPhaseID(null);
		}
	};

	const bqePhaseSelected = (event) => {
		const selectedPhaseID = event.target.value;
		clearFieldError("bqePhase");

		if (selectedPhaseID) {
			setBQEPhaseID(selectedPhaseID);
		} else {
			setBQEPhaseID(null);
		}
	};

	const handleAdjustmentAmountChange = (event) => {
		const value = event.target.value;
		clearFieldError("amendmentAdjustmentAmount");
		setAmendmentAdjustmentAmount(value);
	};

	const handleContractTypeChange = (event) => {
		clearFieldError("contractType");
		setContractType(event.target.value);
	};

	const handleAdjustmentLocationChange = (event) => {
		clearFieldError("adjustmentLocation");
		setAdjustmentLocation(event.target.value);
	};

	const handleNotesChange = (event) => {
		setPendingAmendmentRecord((prev) => ({
			...prev,
			notes: event.target.value,
		}));
	};

	const clearFieldError = (fieldName) => {
		setFieldErrors((prev) => {
			if (!prev[fieldName]) {
				return prev;
			}

			const next = { ...prev };
			delete next[fieldName];
			return next;
		});
	};

	const submitPendingAmendment = async () => {
		const isTimeAndMaterials = contractType === "time_and_cost";
		const actionType = isTimeAndMaterials ? "mark_tm_done" : "submit";
		const actionClickedTime = getCurrentTimeUTC();
		const stepOutputs = {};

		const handleAmendmentSubmitFailure = async (
			errorMessage,
			alertMessage = null
		) => {
			await submitPendingAmendmentFormLog({
				actionType,
				actionStatus: "failure",
				errorMessage,
				actionClickedTime,
				actionCompletedTime: getCurrentTimeUTC(),
				stepOutputs,
			});
			if (alertMessage) {
				setAlertMessage(alertMessage);
				setAlertType("error");
			}
			setIsSubmitted(false);
		};

		if (!validateInput()) {
			await handleAmendmentSubmitFailure("Validation failed.");
			return;
		}

		const submittingMessage = isTimeAndMaterials
			? "Marking pending amendment as T&M and completed..."
			: "Submitting pending amendment...";

		setAlertMessage(submittingMessage);
		setAlertType("info");

		setIsSubmitted(true);

		try {
			let contractAmountPreSubmission =
				pendingAmendmentRecord.contract_amount_pre_submission ?? null;
			let newPhaseCreated = false;
			let contractAmountAdjusted = false;

			if (adjustmentLocation === "existing_phase") {
				const targetPhase = bqePhaseOptions.find((p) => p.id === bqePhaseID);
				contractAmountPreSubmission = getContractAmountPreSubmission({ targetPhase });

				const updateSuccess = await updateBQEPhaseAmount(targetPhase);
				stepOutputs.updateBQEPhaseAmount = summarizeOutput(updateSuccess);

				if (!updateSuccess) {
					await handleAmendmentSubmitFailure(
						"Failed to update the BQE Phase.",
						"Failed to update the BQE Phase."
					);
					return;
				}

				contractAmountAdjusted = true;
			} else {
				const existingAmendmentPhase = bqePhaseOptions.find((p) => p.displayName?.includes("Amendment"));

				if (existingAmendmentPhase) {
					contractAmountPreSubmission = getContractAmountPreSubmission({ targetPhase: existingAmendmentPhase });

					const updateSuccess = await updateBQEPhaseAmount(existingAmendmentPhase);
					stepOutputs.updateBQEPhaseAmount = summarizeOutput(updateSuccess);

					if (!updateSuccess) {
						await handleAmendmentSubmitFailure(
							"Failed to update the BQE Phase.",
							"Failed to update the BQE Phase."
						);
						return;
					}

					contractAmountAdjusted = true;
				} else {
					contractAmountPreSubmission = getContractAmountPreSubmission({ isCreatingNewPhase: true });

					const creationSuccess = await createNewBQEPhase();
					stepOutputs.createNewBQEPhase = summarizeOutput(creationSuccess);

					if (!creationSuccess) {
						await handleAmendmentSubmitFailure(
							"Failed to create the BQE Phase.",
							"Failed to create the BQE Phase."
						);
						return;
					}

					newPhaseCreated = true;
				}
			}

			const dealOwnerID = await fetchDealOwnerID(
				pendingAmendmentRecord.hubspot_deal_id
			);
			stepOutputs.hubspotDealOwnerID = dealOwnerID;

			if (!dealOwnerID) {
				await handleAmendmentSubmitFailure(
					"Failed to resolve HubSpot deal owner.",
					"Failed to resolve HubSpot deal owner."
				);
				return;
			}

			const dealOwnerNotificationRes = await notifyDealOwner({
				propertyID: propertyRecord?.id ?? null,
				projectID: projectRecord?.id ?? null,
				phaseID: phaseRecord?.id ?? null,
				dealOwnerID,
				amendmentAmount: amendmentAdjustmentAmount || null,
				isTimeAndMaterials,
				notes: pendingAmendmentRecord.notes?.trim() || null,
				submittedViaPending: true,
			});
			stepOutputs.dealOwnerNotification =
				summarizeOutput(dealOwnerNotificationRes);

			if (
				!dealOwnerNotificationRes ||
				dealOwnerNotificationRes?.data?.error
			) {
				await handleAmendmentSubmitFailure(
					dealOwnerNotificationRes?.data?.error ||
						"Failed to notify deal owner.",
					"Failed to notify deal owner."
				);
				return;
			}

			// Update the pending amendment record
			const pendingAmendmentUpdated = await updatePendingAmendmentRecord({
				isTimeAndMaterials,
				contractAmountPreSubmission,
				newPhaseCreated,
				contractAmountAdjusted,
			});
			stepOutputs.pendingAmendmentUpdated =
				summarizeOutput(pendingAmendmentUpdated);

			if (!pendingAmendmentUpdated) {
				await handleAmendmentSubmitFailure(
					"Failed to update pending amendment record."
				);
				return;
			}

			await submitPendingAmendmentFormLog({
				actionType,
				actionStatus: "success",
				actionClickedTime,
				actionCompletedTime: getCurrentTimeUTC(),
				contractAmountPreSubmission,
				phaseID: phaseRecord?.id ?? null,
				bqePhaseID: bqePhaseID ?? null,
				amendmentAmount: amendmentAdjustmentAmount || null,
				submittedOn: getCurrentTimeUTC(),
				submittedBy: employeeID ?? null,
				selectedPhaseID: phaseRecord?.id ?? null,
				selectedBqePhaseID: bqePhaseID ?? null,
				newPhaseCreated,
				contractAmountAdjusted,
				stepOutputs,
			});

			navigate("/jobs/amendments/pending"); // falls off the list because is_submitted=1
		} catch (error) {
			stepOutputs.unexpectedError = summarizeOutput(error);
			await handleAmendmentSubmitFailure(
				error?.message ||
					"Unexpected error while submitting pending amendment.",
				"Unexpected error while submitting pending amendment."
			);
			console.error(error);
		}
	};

	const handleSubmit = async () => {
		await submitPendingAmendment();
	};

	// API METHODS
	const fetchPendingAmendment = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/pending/amendments/byID",
				{ id: id } // Example ID, replace with actual ID as needed
			);

			setPendingAmendmentRecord(res.data[0]);
			setAmendmentAdjustmentAmount(res.data[0]?.amendment_amount ?? "");
			setAdjustmentLocation(res.data[0]?.adjustment_location || "");

			return res.data[0];
		} catch (error) {
			console.error(error);
		}
	};

	const fetchPropertyList = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/property/all"
			);
			setAllProperties(res.data);

			return res.data;
		} catch (error) {
			console.error("Error fetching property list:", error);
			return [];
		}
	};

	const fetchProjectList = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/project/all"
			);
			setAllProjects(res.data);

			return res.data;
		} catch (error) {
			console.error("Error fetching property list:", error);
			return [];
		}
	};

	const fetchPhaseList = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/phases/all"
			);
			setAllPhases(res.data);

			return res.data;
		} catch (error) {
			console.error("Error fetching phase list:", error);
			return [];
		}
	};

	const fetchBQEProperty = async (bqeID) => {
		if (!bqeID) {
			setBqePropertyRecord(null);
			return;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/property/byID",
				{
					id: bqeID,
				}
			);

			setBqePropertyRecord(res.data);
			return res.data;
		} catch (error) {
			console.error("Error fetching BQE PROPERTY:", error);
			return [];
		}
	};

	const fetchBQEProjects = async (bqeID) => {
		if (!bqeID) {
			setBqeProjectOptions([]);
			setBqePhaseOptions([]);
			setBQEPhaseID(null);
			return [];
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/byProperty",
				{
					propertyID: bqeID,
				}
			);

			// Filter out deleted projects
			const projectOptions =
				res.data == ""
					? []
					: res.data.filter(
							(option) =>
								!option.displayName.endsWith("(deleted)")
					  );

			setBqeProjectOptions(projectOptions);
			return projectOptions;
		} catch (error) {
			return [];
		}
	};

	const fetchBQEProjectByID = async (projectID) => {
		if (!projectID) {
			return null;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/byID",
				{
					id: projectID,
				}
			);

			return res.data;
		} catch (error) {
			console.error("Error fetching BQE project by ID:", error);
			return null;
		}
	};

	const fetchBQEPhases = async (projectID) => {
		if (!projectID) {
			setBqePhaseOptions([]);
			return [];
		}

		setIsBQEPhaseLoading(true);

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/phases/byProject",
				{
					projectID,
				}
			);

			const phases =
				res.data == ""
					? []
					: res.data.filter(
							(option) =>
								!option.displayName.endsWith("(deleted)")
					  );

			setBqePhaseOptions(phases);
			return phases;
		} catch (error) {
			console.error("Error fetching BQE phases:", error);
			setBqePhaseOptions([]);
			return [];
		} finally {
			setIsBQEPhaseLoading(false);
		}
	};

	const fetchAmendmentPhase = async (bqeProjectID) => {
		// This function checks to see if a phase already exxists in the BQE Project and returns it. If not found, it returns null.
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/phases/byProject",
				{
					projectID: bqeProjectID,
				}
			);

			if (res.data == "") {
				return null;
			}
			let undeletedPhases = res.data.filter(
				(option) => !option.displayName.endsWith("(deleted)")
			);

			return undeletedPhases.find((phase) =>
				phase.displayName.includes("Amendment")
			); // contractAmount is what we need
		} catch (error) {
			console.error("Error fetching BQE phases:", error);
			return [];
		}
	};

	const getContractAmountPreSubmission = ({
		targetPhase = null,
		isCreatingNewPhase = false,
	} = {}) => {
		if (isCreatingNewPhase) {
			return 0;
		}

		return targetPhase?.contractAmount ?? 0;
	};

	const updateBQEPhaseAmount = async (existingAmendmentPhase) => {
		try {
			let updatedAmount =
				existingAmendmentPhase.contractAmount +
				Number(amendmentAdjustmentAmount);

			let updateRes = await putAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/update/amount",
				{
					projectId: existingAmendmentPhase.id,
					contractAmount: updatedAmount,
					version: existingAmendmentPhase.version,
				}
			);

			if (updateRes.status !== 200) {
				return false;
			}

			return true;
		} catch (error) {
			console.error("Error updating BQE Phase amount:", error);

			return false;
		}
	};

	const fetchDealOwnerID = async (hubspotDealID) => {
		if (!hubspotDealID) {
			return null;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hubspot/getDealFromDealID",
				{
					dealID: hubspotDealID,
				}
			);

			return res?.data?.properties?.hubspot_owner_id ?? null;
		} catch (error) {
			console.error("Error fetching HubSpot deal owner:", error);
			return null;
		}
	};

	const notifyDealOwner = async ({
		propertyID = null,
		projectID = null,
		phaseID = null,
		dealOwnerID = null,
		amendmentAmount = null,
		isTimeAndMaterials = false,
		notes = null,
		submittedViaPending = true,
	} = {}) => {
		try {
			return await postAPICall(
				instance,
				accounts[0],
				"/api/outlook/pendingAmendment/emailDealOwner",
				{
					propertyID,
					projectID,
					phaseID,
					dealOwnerID,
					amendmentAmount,
					isTimeAndMaterials,
					notes,
					submittedViaPending,
				}
			);
		} catch (error) {
			console.error("Error notifying deal owner:", error);
			return null;
		}
	};

	const createNewBQEPhase = async () => {
		try {
			let bqeProject = bqeProjectOptions.find(
				(project) => project.id === bqeProjectID
			);

			if (!bqeProject) {
				setAlertMessage("Please select a valid BQE Project.");
				setAlertType("error");
				return;
			}

			let creationRes = await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/phase/create",
				{
					clientId: bqeProject.clientId,
					phaseCode: `PH-${generateAlphanumericCode()}`,
					managerId: bqeProject.managerId,
					contractType: contractType === "fixed_fee" ? 1 : 0,
					phaseName: "Amendment",
					addressLine1: propertyRecord.address_line1,
					addressLine2: propertyRecord.address_line2,
					city: propertyRecord.city,
					state: propertyRecord.state,
					zip: propertyRecord.zip,
					oic: propertyRecord.oic,
					classId: bqeProject.classId,
					parentId: bqeProject.id,
					rootId: bqeProject.rootProjectId,
					contractAmount: amendmentAdjustmentAmount,
				}
			);

			if (creationRes.status !== 200) {
				return false;
			}

			if (creationRes.data?.id) {
				await associateOICCustomField(
					creationRes.data.id,
					propertyRecord.oic
				);
			}

			return true;
		} catch (error) {
			console.error("Error updating BQE Phase amount:", error);

			return false;
		}
	};

	const updatePendingAmendmentRecord = async ({
		isTimeAndMaterials = false,
		contractAmountPreSubmission = null,
		newPhaseCreated = false,
		contractAmountAdjusted = false,
	} = {}) => {
		const endpoint = isTimeAndMaterials
			? "/api/jobs/pending/amendments/markTmDone"
			: "/api/jobs/pending/amendments/submitted";
		const successMessage = isTimeAndMaterials
			? "Pending amendment record marked as T&M and completed."
			: "Pending amendment record updated successfully.";
		const failureMessage = isTimeAndMaterials
			? "Failed to mark pending amendment as T&M."
			: "Failed to update pending amendment record.";
		const actionDescription = isTimeAndMaterials
			? "marking pending amendment as T&M"
			: "updating pending amendment record";

		try {
			const updateRes = await putAPICall(
				instance,
				accounts[0],
				endpoint,
				{
					id: pendingAmendmentRecord.id,
					submittedByID: employeeID,
					propertyID: propertyRecord?.id ?? null,
					projectID: projectRecord?.id ?? null,
					bqePropertyID: bqePropertyRecord?.id ?? null,
					bqeProjectID: bqeProjectID ?? null,
					phaseID: phaseRecord?.id ?? null,
					bqePhaseID: bqePhaseID ?? null,
					isTimeAndMaterials: isTimeAndMaterials,
					contractAmountPreSubmission,
					amendmentAmount: amendmentAdjustmentAmount || null,
					notes: pendingAmendmentRecord.notes?.trim() || null,
					adjustmentLocation: adjustmentLocation || null,
					newPhaseCreated,
					contractAmountAdjusted,
				}
			);

			if (updateRes.status === 200) {
				setAlertMessage(successMessage);
				setAlertType("success");
				return true;
			} else {
				setAlertMessage(failureMessage);
				setAlertType("error");
				return false;
			}
		} catch (error) {
			console.error(`Error ${actionDescription}:`, error);
			setAlertMessage(`Error ${actionDescription}.`);
			setAlertType("error");
			return false;
		}
	};

	// HELPER METHODS
	const fetchInitialData = async () => {
		setIsLoading(true);

		let amendment = await fetchPendingAmendment();
		let properties = await fetchPropertyList();
		let projects = await fetchProjectList();
		let phases = await fetchPhaseList();

		if (amendment) {
			const property = properties.find(
				(property) => property.id === amendment.property_id
			);
			setPropertyRecord(property || null);

			const project = projects.find(
				(project) => project.id === amendment.project_id
			);
			setProjectRecord(project || null);

			const phase = phases.find((phase) => phase.id === amendment.phase_id);
			setPhaseRecord(phase || null);
		}

		let bqeProperty = await fetchBQEProperty(
			amendment?.bqe_property_id || null
		);

		const bqeProjects = await fetchBQEProjects(
			amendment?.bqe_property_id || null
		);

		if (bqeProperty) {
			setBqePropertyRecord(bqeProperty);
			setBqePropertySearchValue(bqeProperty.displayName);
		}

		if (amendment?.bqe_project_id) {
			const hasStoredProject = bqeProjects.some(
				(project) => project.id === amendment.bqe_project_id
			);

			if (!hasStoredProject) {
				const fallbackProject = await fetchBQEProjectByID(
					amendment.bqe_project_id
				);

				if (fallbackProject) {
					setBqeProjectOptions((prev) => {
						if (prev.some((project) => project.id === fallbackProject.id)) {
							return prev;
						}
						return [...prev, fallbackProject];
					});
				}
			}

			setBQEProjectID(amendment.bqe_project_id);
		}

		const bqePhases = await fetchBQEPhases(amendment?.bqe_project_id || null);
		if (bqePhases.length > 0) {
			setBQEPhaseID(amendment?.bqe_phase_id || null);
		}

		setIsLoading(false);
	};

	// Standard debounce function
	function debounce(callback, wait) {
		let timeoutId = null;
		return (...args) => {
			window.clearTimeout(timeoutId);
			timeoutId = window.setTimeout(() => {
				callback.apply(null, args);
			}, wait);
		};
	}

	const associateOICCustomField = async (projectID, oic) => {
		try {
			let bqeOICValue = null; // This is the ID of the dropdown list value for the OIC in our system

			if (oic === "WA") {
				bqeOICValue = "d5e226fe-5798-43a4-a752-7bcd087eeda3"; // WA OIC dropdown ID
			} else if (oic === "OR") {
				bqeOICValue = "03356b2a-8467-46ef-9ce7-cf4dc99599d3"; // OR OIC dropdown ID
			} else if (oic === "UT") {
				bqeOICValue = "60d08179-1541-41ee-938f-9415ba4b5872"; // UT OIC dropdown ID
			} else {
				bqeOICValue = "d5e226fe-5798-43a4-a752-7bcd087eeda3"; // Default to WA OIC dropdown ID
			}

			await postAPICall(
				instance,
				accounts[0],
				"/api/bqe/oic/assign",
				{
					bqeProjectId: projectID,
					bqeOICValue: bqeOICValue,
				}
			);
		} catch (err) {
			console.error("Error assigning OIC custom field:", err);
		}
	};

	const validateInput = () => {
		const nextFieldErrors = {};
		const addIssue = (fieldName, message) => {
			nextFieldErrors[fieldName] = message;
		};

		if (!propertyRecord) {
			addIssue("property", "Please select a property.");
		}

		if (!projectRecord) {
			addIssue("project", "Please select a project.");
		}

		if (!phaseRecord) {
			addIssue("phase", "Please select a phase.");
		}

		if (!amendmentAdjustmentAmount) {
			addIssue(
				"amendmentAdjustmentAmount",
				"Please enter an adjustment amount."
			);
		}

		if (
			amendmentAdjustmentAmount &&
			!String(amendmentAdjustmentAmount).match(integerRegex)
		) {
			addIssue(
				"amendmentAdjustmentAmount",
				"Adjustment amount must be a valid whole number."
			);
		}

		if (!bqePropertyRecord) {
			addIssue("bqeProperty", "Please select a BQE Property.");
		}

		if (!bqeProjectID) {
			addIssue("bqeProject", "Please select a BQE Project.");
		}

		if (!bqePhaseID) {
			addIssue("bqePhase", "Please select a BQE Phase.");
		}

		if (!contractType) {
			addIssue("contractType", "Please select a contract type.");
		}

		if (!adjustmentLocation) {
			addIssue("adjustmentLocation", "Please select an adjustment location.");
		}

		setFieldErrors(nextFieldErrors);

		if (Object.keys(nextFieldErrors).length > 0) {
			setAlertMessage("Please complete all required fields.");
			setAlertType("error");
			return false;
		}

		return true;
	};

	if (!isLoading && pendingAmendmentRecord?.is_submitted) {
		return (
			<div className="PendingAmendmentReviewScreen">
				<div className="pendingAmendmentReviewScreenHeader">
					{pendingAmendmentRecord?.hubspot_deal_name}
				</div>
				<div className="pendingAmendmentReviewScreenContainer">
					<div className="pendingJobAlreadySubmittedMessage">
						This job has already been submitted.
					</div>
				</div>
			</div>
		);
	}

	// RENDER
	return (
		<div className="PendingAmendmentReviewScreen">
			<div className="pendingAmendmentReviewScreenHeader">
				{pendingAmendmentRecord?.hubspot_deal_name}
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress />
				</div>
			)}
			{!isLoading && (
				<div className="pendingAmendmentReviewScreenContainer">
					<div className="pendingAmendmentReviewScreenCard">
						<div className="pendingAmendmentReviewScreenCardHeader">
							General Information
						</div>
						<div className="pendingAmendmentReviewScreenCardRow">
							<Autocomplete
								sx={{ flexGrow: 1 }}
								id="property"
								options={allProperties}
								getOptionLabel={(option) =>
									option ? option.name : ""
								}
								value={propertyRecord}
								onChange={handlePropertyChange}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Property"
										variant="outlined"
										error={Boolean(fieldErrors.property)}
										helperText={fieldErrors.property || ""}
									/>
								)}
							/>
						</div>
						<div className="pendingAmendmentReviewScreenCardRow">
							<Autocomplete
								sx={{ flexGrow: 1 }}
								id="project"
								options={allProjects.filter(
									(project) =>
										project.property_id ===
										propertyRecord?.id
								)}
								getOptionLabel={formatNameWithYear}
								disabled={!propertyRecord}
								value={projectRecord}
								onChange={handleProjectChange}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Project"
										variant="outlined"
										error={Boolean(fieldErrors.project)}
										helperText={fieldErrors.project || ""}
									/>
								)}
							/>
						</div>
						<div className="pendingAmendmentReviewScreenCardRow">
							<Autocomplete
								sx={{ flexGrow: 1 }}
								id="phase"
								options={allPhases.filter(
									(phase) =>
										phase.property_id ===
											propertyRecord?.id &&
										phase.project_id === projectRecord?.id
								)}
								getOptionLabel={formatNameWithYear}
								disabled={!propertyRecord || !projectRecord}
								value={phaseRecord}
								onChange={handlePhaseChange}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Phase"
										variant="outlined"
										error={Boolean(fieldErrors.phase)}
										helperText={fieldErrors.phase || ""}
									/>
								)}
							/>
						</div>
						<div className="pendingAmendmentReviewScreenCardRow">
							<TextField
								sx={{ flexGrow: 1 }}
								label="Amendment Adjustment Amount"
								value={amendmentAdjustmentAmount}
								onChange={handleAdjustmentAmountChange}
								variant="outlined"
								error={Boolean(fieldErrors.amendmentAdjustmentAmount)}
								helperText={
									fieldErrors.amendmentAdjustmentAmount || ""
								}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<Tooltip title="If amount is a range, put MAX amount">
												<InfoOutlinedIcon
													fontSize="small"
													sx={{
														color: "#6b7280",
														cursor: "help",
													}}
												/>
											</Tooltip>
										</InputAdornment>
									),
								}}
							/>
						</div>
						<div className="pendingAmendmentReviewScreenCardRow">
							<TextField
								sx={{ flex: 1 }}
								label="Notes"
								value={pendingAmendmentRecord.notes || ""}
								onChange={handleNotesChange}
								multiline
								minRows={4}
							/>
						</div>
					</div>
					<div className="pendingAmendmentReviewScreenCardColumn">
						<div className="pendingAmendmentReviewScreenCard">
							<div className="pendingAmendmentReviewScreenCardHeader">
								BQE Information
							</div>
							<div className="pendingAmendmentReviewScreenCardRow">
								<Autocomplete
									sx={{ flex: 1 }}
									options={bqePropertyOptions}
									// ref={propertyNameRef}
									getOptionLabel={(option) =>
										option.displayName ?? ""
									}
									filterSelectedOptions
									value={bqePropertyRecord}
									onChange={bqePropertySelected}
									clearOnBlur={false}
									loading={isBQEPropertyLoading}
									renderInput={(params) => (
										<TextField
											{...params}
											label="BQE Property"
											placeholder="BQE Property"
											value={bqePropertySearchValue || ""}
											onKeyUp={bqePropertyNameChanged}
											error={Boolean(fieldErrors.bqeProperty)}
											helperText={fieldErrors.bqeProperty || ""}
										/>
									)}
								/>
							</div>
							<div className="pendingAmendmentReviewScreenCardRow">
								<FormControl
									sx={{ flex: 1 }}
									error={Boolean(fieldErrors.bqeProject)}
								>
									<InputLabel id="projectRecordLabel">
										Project
									</InputLabel>
									<Select
										labelId="projectRecordLabel"
										value={bqeProjectID || ""}
										label="Project"
										onChange={bqeProjectSelected}
									>
										{bqeProjectOptions.map((option) => (
											<MenuItem
												key={option.id}
												value={option.id}
											>
												{option.displayName}
											</MenuItem>
										))}
									</Select>
									{fieldErrors.bqeProject && (
										<FormHelperText>
											{fieldErrors.bqeProject}
										</FormHelperText>
									)}
								</FormControl>
							</div>
							<div className="pendingAmendmentReviewScreenCardRow">
								<FormControl
									sx={{ flex: 1 }}
									error={Boolean(fieldErrors.bqePhase)}
								>
									<InputLabel id="bqePhaseRecordLabel">
										Phase
									</InputLabel>
									<Select
										labelId="bqePhaseRecordLabel"
										value={bqePhaseID || ""}
										label="Phase"
										onChange={bqePhaseSelected}
										disabled={!bqeProjectID}
										endAdornment={
											<InputAdornment position="end" sx={{ mr: 3 }}>
												<Tooltip title="Choose the phase associated with the amendment, this is NOT where the adjustment amount is going unless you select 'Existing Phase' for Adjustment Location">
													<InfoOutlinedIcon
														fontSize="small"
														sx={{ color: "#6b7280", cursor: "help" }}
													/>
												</Tooltip>
											</InputAdornment>
										}
									>
										{bqePhaseOptions.filter((option) => !option.displayName?.includes("Amendment")).map((option) => (
											<MenuItem
												key={option.id}
												value={option.id}
											>
												{option.displayName}
											</MenuItem>
										))}
									</Select>
									{fieldErrors.bqePhase && (
										<FormHelperText>
											{fieldErrors.bqePhase}
										</FormHelperText>
									)}
								</FormControl>
							</div>
							<div className="pendingAmendmentReviewScreenCardRow" style={{ gap: 16 }}>
								<FormControl sx={{ flex: 1 }} error={Boolean(fieldErrors.contractType)}>
									<InputLabel id="pendingAmendmentContractTypeLabel">Contract Type</InputLabel>
									<Select
										labelId="pendingAmendmentContractTypeLabel"
										value={contractType}
										label="Contract Type"
										onChange={handleContractTypeChange}
									>
										<MenuItem value="fixed_fee">Fixed Fee</MenuItem>
										<MenuItem value="time_and_cost">Time &amp; Cost</MenuItem>
									</Select>
									{fieldErrors.contractType && (
										<FormHelperText>{fieldErrors.contractType}</FormHelperText>
									)}
								</FormControl>
								<FormControl sx={{ flex: 1 }} error={Boolean(fieldErrors.adjustmentLocation)}>
									<InputLabel id="pendingAmendmentAdjustmentLocationLabel">Adjustment Location</InputLabel>
									<Select
										labelId="pendingAmendmentAdjustmentLocationLabel"
										value={adjustmentLocation}
										label="Adjustment Location"
										onChange={handleAdjustmentLocationChange}
									>
										<MenuItem value="existing_phase">Add to Selected Existing Phase</MenuItem>
										<MenuItem value="amendment_phase">Amendment Phase</MenuItem>
									</Select>
									{fieldErrors.adjustmentLocation && (
										<FormHelperText>{fieldErrors.adjustmentLocation}</FormHelperText>
									)}
								</FormControl>
							</div>
							{showContractTypeMismatch && (
								<Alert severity="warning" sx={{ mt: 1, alignSelf: "stretch" }}>
									The selected existing BQE phase has a contract type that doesn't match your selection.
								</Alert>
							)}
							{!showContractTypeMismatch && adjustmentInfoMessage && (
								<Alert severity="info" sx={{ mt: 1, alignSelf: "stretch" }}>
									{adjustmentInfoMessage}
								</Alert>
							)}
						</div>
					</div>
				</div>
			)}
			<div className="pendingAmendmentReviewScreenActions">
				{alertMessage && (
					<Alert
						sx={{ flex: 1 }}
						severity={alertType}
						onClose={() => setAlertMessage("")}
					>
						{alertMessage}
					</Alert>
				)}
				<Button
					variant="contained"
					color="primary"
					onClick={handleSubmit}
					disabled={isSubmitted || isLoading || showContractTypeMismatch}
				>
					Submit
				</Button>
			</div>
		</div>
	);
};

export default PendingAmendmentReviewScreen;
